import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ArrowUpRight, Clock, CalendarCheck, Sparkle } from "@phosphor-icons/react";
import { CLINIC, TREATMENTS } from "../data/site";
import { EnquiryDialog } from "./EnquiryDialog";

export const TreatmentList = ({ category }) => {
  const items = TREATMENTS.filter((t) => t.category === category);
  return (
    <Accordion type="single" collapsible className="w-full" data-testid={`treatment-accordion-${category}`}>
      {items.map((t, i) => (
        <AccordionItem key={t.id} value={t.id} id={t.id} className="scroll-mt-28 border-border">
          <AccordionTrigger
            data-testid={`treatment-trigger-${t.id}`}
            className="group py-6 text-left hover:no-underline [&>svg]:text-primary"
          >
            <span className="flex w-full flex-col gap-2 pr-4 sm:flex-row sm:items-baseline sm:justify-between">
              <span className="flex items-baseline gap-4">
                <span className="font-display text-sm italic text-primary/60">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-2xl font-light tracking-tight transition-colors duration-300 group-hover:text-primary sm:text-3xl">
                  {t.name}
                </span>
                {t.isNew && (
                  <span className="border border-primary/30 bg-accent px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.2em] text-primary">
                    New
                  </span>
                )}
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {t.pricing[0]?.price}{t.pricing.length > 1 ? "+" : ""}
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 gap-10 pb-6 pl-0 md:grid-cols-12 md:pl-12">
              <div className="md:col-span-7">
                <p className="font-display text-lg italic text-primary">{t.tag}</p>
                <p className="mt-3 text-sm font-light leading-relaxed text-foreground/85">{t.about}</p>
                {t.benefits?.length > 0 && (
                  <ul className="mt-5 space-y-2">
                    {t.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm font-light">
                        <Sparkle size={14} weight="fill" className="mt-1 shrink-0 text-primary" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {t.faq?.length > 0 && (
                  <div className="mt-7 border-t border-border/70 pt-5" data-testid={`treatment-faq-${t.id}`}>
                    <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-primary">Common questions</p>
                    <dl className="mt-4 space-y-4">
                      {t.faq.map((f) => (
                        <div key={f.q}>
                          <dt className="text-sm font-medium">{f.q}</dt>
                          <dd className="mt-1 text-sm font-light leading-relaxed text-muted-foreground">{f.a}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
              <div className="md:col-span-5">
                <div className="border border-border bg-secondary/50 p-6">
                  <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-primary">Pricing</p>
                  <ul className="mt-3 divide-y divide-border/70">
                    {t.pricing.map((p) => (
                      <li key={p.label} className="flex items-baseline justify-between gap-4 py-2.5">
                        <span className="text-sm font-light text-foreground/80">{p.label}</span>
                        <span className="font-display text-xl">{p.price}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 grid grid-cols-2 gap-4 border-t border-border/70 pt-4">
                    <div>
                      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        <Clock size={12} /> Downtime
                      </p>
                      <p className="mt-1 text-sm font-light">{t.downtime}</p>
                    </div>
                    <div>
                      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        <CalendarCheck size={12} /> Lasts
                      </p>
                      <p className="mt-1 text-sm font-light">{t.lasts}</p>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-col gap-2.5">
                    {t.externalUrl ? (
                      <a
                        href={t.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`treatment-shop-${t.id}`}
                        className="flex items-center justify-center gap-2 bg-primary px-5 py-3 text-[11px] font-medium uppercase tracking-[0.25em] text-primary-foreground transition-colors duration-300 hover:bg-primary/90"
                      >
                        {t.ctaLabel || "Explore IV menu & book"} <ArrowUpRight size={14} />
                      </a>
                    ) : (
                      <EnquiryDialog
                        defaultTreatment={t.name}
                        defaultType="appointment"
                        trigger={
                          <button
                            data-testid={`treatment-book-${t.id}`}
                            className="flex w-full items-center justify-center gap-2 bg-primary px-5 py-3 text-[11px] font-medium uppercase tracking-[0.25em] text-primary-foreground transition-colors duration-300 hover:bg-primary/90"
                          >
                            Book appointment <ArrowUpRight size={14} />
                          </button>
                        }
                      />
                    )}
                    <EnquiryDialog
                      defaultTreatment={t.name}
                      defaultType="consultation"
                      trigger={
                        <button
                          data-testid={`treatment-enquire-${t.id}`}
                          className="border border-border px-5 py-3 text-[11px] font-medium uppercase tracking-[0.25em] transition-colors duration-300 hover:border-primary hover:text-primary"
                        >
                          Free consultation
                        </button>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
